import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { ThemeMode, ThemeStyle, ThemeState } from '@/types'

export const useThemeStore = defineStore('theme', () => {
  // State
  const mode = ref<ThemeMode>('dark')
  const style = ref<ThemeStyle>('glassmorphism')

  // Getters
  const isDark = computed(() => mode.value === 'dark')
  const isLight = computed(() => mode.value === 'light')
  const isGlassmorphism = computed(() => style.value === 'glassmorphism')
  const isShadcn = computed(() => style.value === 'shadcn')

  const themeClasses = computed(() => {
    return {
      'dark': isDark.value,
      'glassmorphism': isGlassmorphism.value,
      'shadcn': isShadcn.value,
    }
  })

  // Actions
  function setMode(newMode: ThemeMode) {
    mode.value = newMode
    saveToStorage()
    applyTheme()
  }

  function toggleMode() {
    mode.value = isDark.value ? 'light' : 'dark'
    saveToStorage()
    applyTheme()
  }

  function setStyle(newStyle: ThemeStyle) {
    style.value = newStyle
    saveToStorage()
    applyTheme()
  }

  function saveToStorage() {
    const state: ThemeState = {
      mode: mode.value,
      style: style.value,
    }
    localStorage.setItem('kardapio-theme', JSON.stringify(state))
  }

  function loadFromStorage() {
    const stored = localStorage.getItem('kardapio-theme')
    if (stored) {
      try {
        const state: ThemeState = JSON.parse(stored)
        mode.value = state.mode
        style.value = state.style
      } catch {
        // Invalid storage, ignore
      }
    }
  }

  function applyTheme() {
    // Apply mode
    if (isDark.value) {
      document.documentElement.classList.add('dark')
      document.documentElement.classList.remove('light')
    } else {
      document.documentElement.classList.remove('dark')
      document.documentElement.classList.add('light')
    }

    // Apply CSS variables based on style
    const root = document.documentElement
    
    if (isGlassmorphism.value) {
      root.style.setProperty('--glass-bg', isDark.value 
        ? 'rgba(17, 24, 39, 0.7)' 
        : 'rgba(255, 255, 255, 0.7)')
      root.style.setProperty('--glass-border', isDark.value 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(0, 0, 0, 0.1)')
      root.style.setProperty('--glass-shadow', isDark.value 
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' 
        : '0 8px 32px 0 rgba(31, 38, 135, 0.15)')
    } else {
      // Shadcn style - reset glassmorphism vars
      root.style.removeProperty('--glass-bg')
      root.style.removeProperty('--glass-border')
      root.style.removeProperty('--glass-shadow')
    }
  }

  function initTheme() {
    loadFromStorage()
    applyTheme()
  }

  return {
    mode,
    style,
    isDark,
    isLight,
    isGlassmorphism,
    isShadcn,
    themeClasses,
    setMode,
    toggleMode,
    setStyle,
    initTheme,
  }
})
